import * as i0 from '@angular/core';
import { forwardRef, Directive, HostListener, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import moment from 'moment';
import { DateTime } from 'luxon';
import { trimEnd } from 'lodash';

const EmailPasswordPattern = {
    EMAIL: /^(([^<>()[\]\\.,;:\s@]+(\.[^<()[\]\\.,;:\s@]+)*)|(.+))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,4}))$/,
    PASSWORD_REGEX: /^(?!^(.)\1*$)(.{7,})$/,
    PASSWORD_REPEATEDCHAR_REGEX: /^(.)\1+$/,
    PASSWORD_LENGTH_REGEX: /^.{8,}$/,
};
const CreditCardPatterns = {
    VISA: /^4[0-9]{12}(?:[0-9]{3})?$/,
};
const NamePattern = {
    NAME: /^[a-z](?![a-z\.\`\'\-\s]*[\.\`\'\-]{2})(?![a-z\.\`\'\-\s]*[\s]{2})[a-z\.\`\'\-\s]*$/i,
    NAME_SUFFIX: /^[a-z](?![a-z\.\`\'\-\s]*[\.\`\'\-]{2})(?![a-z\.\`\'\-\s]*[\s]{2})[a-z\.\`\'\-\s]*$/i,
};
const NumberCharPattern = {
    NUMBER: /^[0-9]*$/,
    BEGIN_WITH_NUMBER: /[^0-9]/,
    NON_NUMBER: /[^0-9]/g,
    LETTER: /^[a-z].*[A-Z]|[A-Z].*[a-z]*$/,
    SPECIAL_CHAR: /^[-!$@#%^&*()_+|~=`{}\[\]:";'<>?,.\/]*$/,
    NO_SPECIAL_CHARS: /^([a-zA-Z0-9 ]*)$/,
    WITH_NUMBER: /[0-9]/,
    WITH_LETTER: /[a-z].*[A-Z]|[A-Z].*[a-z]/,
    WITH_SPECIAL_CHAR: /[-!$@#%^&*()_+|~=`{}\[\]:";'<>?,.\/]/,
    LETTER_NUMBER: /^[a-zA-Z0-9\s,'\-.]*$/,
    WITH_LOWER_LETTER: /[a-z]/,
    WITH_UPPER_LETTER: /[A-Z]/,
    NON_DIGIT: /\D/g,
    START_WITH_PLUS: /^\+/,
};
const MiscPattern = {
    ENDS_WITH_SPACE: /\s+$/,
    BRACKET_DOUBLEQUOTES: /[\[\]'"]/g,
    DATE: /^(0?[1-9]|1[012])[\/](0?[1-9]|[12][0-9]|3[01])[\/]\d{4}$/,
    ANNUALPASS_DOB: /^(0?[1-9]|1[012])[\/](0?[1-9]|[12][0-9]|3[01])$/,
};
const ZipCodePattern = {
    ZIP: /^(?:\d{5,9}|\d{5}(?:[-]\d{4})?)$/,
    NON_US_ZIP: /^([a-zA-Z0-9\s-]){0,12}$/,
};
const PhonePattern = {
    PHONE: /^(\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4}))+$/,
    NON_US_PHONE: /^[0-9-]+/,
    PHONE_ANY: /^[- +()]*[0-9][- +()0-9]*$/,
};

const formFieldRanges = {
    DEFAULT_COUNTRY: 'US',
    MIN: {
        PASSWORD: 8,
        USA_PHONE: 10,
        USA_ZIP: 5,
        NON_USA_PHONE: 7
    },
    MAX: {
        SUFFIX: 25,
        FIRST_NAME: 25,
        MIDDLE_NAME: 25,
        LAST_NAME: 25,
        PASSWORD: 99,
        USA_PHONE: 10,
        PHONE: 15,
        USA_ZIP: 9,
        NON_USA_ZIP: 12,
        CITY: 50,
        EMAIL: 255,
        ZIP_ENTRY: 20,
        CVV: 4,
        PAYMENT_CARD: 20,
        DATE: 11
    },
    PASSWORDS: {
        PASSWORD_LENGTH_INDEX: 10,
        PASSWORD_FORCE_FACTOR: 10,
        PASSWORD_FLAG_COLORS: ['#F00', '#F90', '#FF0', '#9F0', '#0B0'],
        PASSWORD_FLAG_STRENGTH: [
            'Very Weak',
            'Weak',
            'Medium',
            'Moderate',
            'Strong'
        ],
        DEFAULT_BACKGROUND: '#DDD'
    },
    PHONE: {
        USA_COUNTRY_CODE: 'US',
        ANY_COUNTRY_CODE: 'any'
    }
};

class GdsInputHelperDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
    }
    get value() {
        return this._value;
    }
    set value(val) {
        if (val !== this._value) {
            this._value = val;
            if (this.onChange) {
                this.onChange(this._value);
            }
            this.elementRef.nativeElement.value = val;
        }
    }
    writeValue(value) {
        if (value) {
            this.value = value;
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    listenForValueChange(value) {
        this.value = value;
    }
    listenForBlurChange() {
        if (this.onTouched && this.onChange) {
            this.onTouched();
            this.onChange(this._value);
        }
    }
}
GdsInputHelperDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsInputHelperDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
GdsInputHelperDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: GdsInputHelperDirective, selector: "gds-input", host: { listeners: { "valueChange": "listenForValueChange($event.detail)", "blurChange": "listenForBlurChange()" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => GdsInputHelperDirective),
            multi: true
        }
    ], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsInputHelperDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'gds-input',
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => GdsInputHelperDirective),
                            multi: true
                        }
                    ]
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { listenForValueChange: [{
                type: HostListener,
                args: ['valueChange', ['$event.detail']]
            }], listenForBlurChange: [{
                type: HostListener,
                args: ['blurChange', []]
            }] } });

class GdsInputHelperModule {
}
GdsInputHelperModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsInputHelperModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
GdsInputHelperModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsInputHelperModule, declarations: [GdsInputHelperDirective], exports: [GdsInputHelperDirective] });
GdsInputHelperModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsInputHelperModule, imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsInputHelperModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [GdsInputHelperDirective],
                    imports: [],
                    exports: [GdsInputHelperDirective]
                }]
        }] });

const validatorWithMessage = (validatorFn, errorMessage) => {
    return (control) => {
        const error = validatorFn(control);
        const message = control.touched ? errorMessage : null;
        return error ? { ...error, message } : null;
    };
};
const gdsRequiredValidator = (message) => {
    return validatorWithMessage(Validators.required, message);
};
const gdsMinLengthValidator = (minLength, message) => {
    return validatorWithMessage(Validators.minLength(minLength), message?.replace('##minlength##', minLength?.toString() ?? ''));
};
const gdsMaxLengthValidator = (maxLength, message) => {
    return validatorWithMessage(Validators.maxLength(maxLength), message?.replace('##maxlength##', maxLength?.toString() ?? ''));
};
const gdsPatternValidator = (pattern, message) => {
    return validatorWithMessage(Validators.pattern(pattern), message);
};
const gdsEmailValidator = (requiredError, patternError) => {
    return Validators.compose([
        gdsRequiredValidator(requiredError),
        gdsPatternValidator(EmailPasswordPattern.EMAIL, patternError),
    ]);
};
const gdsVisaCardValidator = (requiredError, patternError) => {
    return Validators.compose([
        gdsRequiredValidator(requiredError),
        gdsPatternValidator(CreditCardPatterns.VISA, patternError),
    ]);
};
const gdsNameValidator = (requiredError, maxLengthError, patternError) => {
    return Validators.compose([
        gdsRequiredValidator(requiredError),
        gdsMaxLengthValidator(formFieldRanges.MAX.LAST_NAME, maxLengthError),
        gdsPatternValidator(NamePattern.NAME, patternError),
    ]);
};
const gdsSuffixValidator = (maxLengthError, patternError) => {
    return Validators.compose([
        gdsMaxLengthValidator(formFieldRanges.MAX.SUFFIX, maxLengthError),
        gdsPatternValidator(NamePattern.NAME_SUFFIX, patternError),
    ]);
};
const gdsPasswordValidator = (requiredError, minLengthError) => {
    return Validators.compose([
        gdsRequiredValidator(requiredError),
        gdsMinLengthValidator(formFieldRanges.MIN.PASSWORD, minLengthError),
    ]);
};
const gdsPhoneNumericValidator = (countryName, numericError) => {
    return validatorWithMessage(NumericValidator(countryName), numericError);
};
const gdsMonthDateValidator = (dateError) => {
    return validatorWithMessage(MomentValidator(), dateError);
};
const NumericValidator = (countryName) => {
    return (control) => {
        let transformedInput = control.value;
        const inProcessInput = [];
        if (countryName && countryName.toLowerCase() === 'us') {
            control.value?.split('').forEach((item, index) => {
                if ((index === 0 && item === '(') ||
                    (index === 4 && item === ')') ||
                    (index === 5 && item === ' ') ||
                    (index === 9 && item === '-'))
                    return;
                inProcessInput.push(item);
            });
        }
        else {
            control.value?.split('').forEach((item) => {
                if (item === '-')
                    return;
                inProcessInput.push(item);
            });
        }
        transformedInput = inProcessInput.join('');
        const validationRegex = new RegExp(NumberCharPattern.NUMBER);
        if (!validationRegex.test(transformedInput) ||
            (!transformedInput &&
                control.value?.length > 0 &&
                transformedInput.length > 0)) {
            return {
                alphaNumeric: true,
            };
        }
        return null;
    };
};
const MomentValidator = () => {
    return (control) => {
        if (control.value) {
            const modelValue = control.value + '-1904';
            const date = moment(modelValue.replace('/', '-'), 'MM-DD-YYYY', true);
            return date.isValid() ? null : { date: true };
        }
        else {
            return null;
        }
    };
};
const DateRangeValidator = (err) => {
    const fn = (control) => {
        const enteredDate = control?.value;
        const parsedDate = DateTime.fromISO(enteredDate);
        const currentDate = DateTime.now();
        const maxPastDate = currentDate.minus({ years: 100 });
        if (parsedDate?.isValid) {
            if (parsedDate >= maxPastDate && parsedDate < currentDate.minus({ days: 1 })) {
                return null;
            }
            return { isValidDate: false };
        }
        else {
            return null;
        }
    };
    return validatorWithMessage(fn, err);
};
const RequiredLabel = (required, label) => {
    return [trimEnd(label, '*')?.trim(), required ? '*' : ''].join(' ');
};

export { CreditCardPatterns, DateRangeValidator, EmailPasswordPattern, GdsInputHelperDirective, GdsInputHelperModule, MiscPattern, MomentValidator, NamePattern, NumberCharPattern, NumericValidator, PhonePattern, RequiredLabel, ZipCodePattern, formFieldRanges, gdsEmailValidator, gdsMaxLengthValidator, gdsMinLengthValidator, gdsMonthDateValidator, gdsNameValidator, gdsPasswordValidator, gdsPatternValidator, gdsPhoneNumericValidator, gdsRequiredValidator, gdsSuffixValidator, gdsVisaCardValidator, validatorWithMessage };
